import React, { useEffect, useRef } from 'react';

function drawText(ctx, text, x, y, font, reverse) {
  ctx.save();
  ctx.font = `${font.size}px sans-serif`;
  const w = ctx.measureText(text).width;
  let drawX = x;
  if (font.align === 'R') drawX = x - w;
  if (font.align === 'C') drawX = x - w / 2;
  if (reverse) {
    ctx.fillStyle = '#000';
    ctx.fillRect(drawX - 2, y - font.size, w + 4, font.size + 6);
    ctx.fillStyle = '#fff';
  } else {
    ctx.fillStyle = '#000';
  }
  ctx.fillText(text, drawX, y);
  ctx.restore();
}

function wrapAndDraw(ctx, text, x, y, font, reverse, blockWidth) {
  if (!blockWidth || blockWidth <= 0) {
    drawText(ctx, text, x, y, font, reverse);
    return font.size + 4;
  }
  const words = String(text).split(/\s+/);
  let line = '';
  let lines = [];
  for (const w of words) {
    const test = line ? line + ' ' + w : w;
    const width = ctx.measureText(test).width;
    if (width > blockWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  let dy = 0;
  for (const l of lines) {
    drawText(ctx, l, x, y + dy, font, reverse);
    dy += font.size + 4;
  }
  return dy;
}

function renderZPL(zpl, canvas) {
  if (!canvas || !zpl) return;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  let LH = { x: 0, y: 0 };
  let cur = { x: 0, y: 0 };
  let font = { size: 20, align: 'L' };
  let reverse = false;
  let orientation = 'N';
  let fbWidth = null;

  const zx = (v) => LH.x + Number(v || 0);
  const zy = (v) => LH.y + Number(v || 0);

  // Determine base label size (^PW / ^LL), default 800x600 dots
  let baseW = 800;
  let baseH = 600;
  try {
    const prelim = zpl.replace(/\r/g, '').replace(/\n/g, '').split('^').filter((p) => p.trim().length);
    for (const s of prelim) {
      const c2 = s.substring(0, 2).toUpperCase();
      const r = s.substring(2);
      if (c2 === 'PW') {
        const w = Number(r.split(',')[0]);
        if (w > 0) baseW = w;
      } else if (c2 === 'LL') {
        const h = Number(r.split(',')[0]);
        if (h > 0) baseH = h;
      }
    }
  } catch {}

  // Scale to fit canvas
  const scaleX = canvas.width / baseW;
  const scaleY = canvas.height / baseH;
  const scale = Math.min(scaleX, scaleY);
  ctx.save();
  ctx.scale(scale, scale);

  const cleaned = zpl.replace(/\r/g, '').replace(/\n/g, '');
  const segments = cleaned.split('^').filter((p) => p.trim().length);

  for (let raw of segments) {
    const cmd2 = raw.substring(0, 2).toUpperCase();
    const rest2 = raw.substring(2);
    // Handle font commands like ^AON, ^AAN, ^A@N etc
    if (raw[0] === 'A') {
      const fontFamily = raw[1];
      const ori = raw[2]?.toUpperCase() || 'N';
      orientation = ori;
      const rest = raw.substring(3);
      const [h] = rest.split(',');
      const size = Number(h);
      if (size) font.size = size;
      continue;
    }
    const cmd = cmd2;
    const rest = rest2;
    switch (cmd) {
      case 'XA':
        LH = { x: 0, y: 0 };
        cur = { x: 0, y: 0 };
        font = { size: 20, align: 'L' };
        reverse = false;
        orientation = 'N';
        fbWidth = null;
        break;
      case 'XZ':
        break;
      case 'PW': {
        const w = Number(rest.split(',')[0]);
        if (w && w > 0) baseW = w;
        break;
      }
      case 'LL': {
        const h = Number(rest.split(',')[0]);
        if (h && h > 0) baseH = h;
        break;
      }
      case 'LH': {
        const [x, y] = rest.split(',');
        LH.x = Number(x || 0);
        LH.y = Number(y || 0);
        break;
      }
      case 'FO': {
        const [x, y] = rest.split(',');
        cur.x = zx(x);
        cur.y = zy(y);
        break;
      }
      case 'FW': {
        orientation = (rest || 'N').charAt(0).toUpperCase();
        break;
      }
      case 'FR':
        reverse = true;
        break;
      case 'GB': {
        const [w, h, th] = rest.split(',');
        ctx.save();
        const lw = Number(th || 1);
        ctx.lineWidth = lw;
        ctx.strokeStyle = '#000';
        ctx.strokeRect(cur.x, cur.y, Number(w), Number(h));
        ctx.restore();
        break;
      }
      case 'FB': {
        const parts = rest.split(',');
        fbWidth = Number(parts[0]) || null;
        font.align = parts[3] || 'L';
        break;
      }
      case 'FD': {
        let text = rest.replace(/\^FS$/, '');
        let advance = 0;
        ctx.save();
        if (orientation === 'R' || orientation === 'B' || orientation === 'I') {
          ctx.translate(cur.x, cur.y);
          let angle = 0;
          if (orientation === 'R') angle = Math.PI / 2;
          if (orientation === 'I') angle = -Math.PI / 2;
          if (orientation === 'B') angle = Math.PI;
          ctx.rotate(angle);
          advance = wrapAndDraw(ctx, text, 0, 0, font, reverse, fbWidth);
        } else {
          advance = wrapAndDraw(ctx, text, cur.x, cur.y, font, reverse, fbWidth);
        }
        ctx.restore();
        reverse = false;
        // After FD, ZPL often expects next field manual FO; we keep position unchanged
        break;
      }
      case 'FS':
        reverse = false;
        fbWidth = null;
        break;
      default:
        break;
    }
  }
  ctx.restore();
}

export default function ZplCanvasRenderer({ zpl, width = 600, height = 400, style }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = width;
    canvas.height = height;
    renderZPL(zpl || '', canvas);
  }, [zpl, width, height]);
  return <canvas ref={canvasRef} width={width} height={height} style={{ background: '#fff', border: '1px solid #ddd', ...(style || {}) }} />;
}